// src/hooks/useTravelRequest.js
import { useState, useEffect, useCallback } from "react"
import {
  getTravelRequests,
  createTravelRequest as apiCreateTravelRequest,
  approveTravelRequest,
  rejectTravelRequest,
  addItineraryToRequest,
  getTravelReports,
} from "../api/travelApi"
import { getRequisitions } from "../api/procurementApi"

const useTravelRequests = () => {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [summary, setSummary] = useState({
    pending: 0,
    approved: 0,
    withItinerary: 0,
    upcoming: 0,
  })

  const [reports, setReports] = useState(null)
  const [reportsLoading, setReportsLoading] = useState(false)
  const [reportsError, setReportsError] = useState("")

  const calculateSummary = useCallback((travelRequests) => {
    const now = new Date()
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)

    const stats = {
      pending: 0,
      approved: 0,
      withItinerary: 0,
      upcoming: 0,
    }

    travelRequests.forEach((request) => {
      const status = request.currentStatus || request.status || "unknown"
      const startDate = new Date(request.startDate || request.travelDetails?.startDate || request.dueDate)

      // Count pending requests
      if (status === "pending" || status === "pending_director_approval" || status === "submitted") {
        stats.pending++
      }

      // Count approved requests
      if (status === "approved" || status === "approved_by_director") {
        stats.approved++
      }

      // Count requests with itinerary
      if (request.itineraries && request.itineraries.length > 0) {
        stats.withItinerary++
      }

      if (
        (status === "approved" || status === "approved_by_director") &&
        !isNaN(startDate.getTime()) &&
        startDate >= now &&
        startDate <= sevenDaysFromNow
      ) {
        stats.upcoming++
      }
    })

    return stats
  }, [])

  const fetchTravelRequests = useCallback(
    async (filters = {}) => {
      setLoading(true)
      setError("")

      try {
        console.log("Fetching travel requests with filters:", filters);
        
        // Initialize arrays for both sources
        let dedicatedTravelRequests = []
        let travelRequisitions = []

        // Fetch from dedicated travel API
        try {
          const travelResponse = await getTravelRequests(filters)
          dedicatedTravelRequests = travelResponse.data?.data || travelResponse || []
          console.log("Dedicated travel requests:", dedicatedTravelRequests.length)
        } catch (travelError) {
          console.warn("Travel API not available, falling back to requisitions:", travelError)
        }

        // Also fetch travel requests from requisitions for compatibility
        try {
          const requisitionFilters = {
            ...filters,
            typeOfProcurement: "travel",
          }
          const requisitionResponse = await getRequisitions(requisitionFilters)
          const allRequisitions = requisitionResponse.data?.data || requisitionResponse || []
          
          console.log("All requisitions:", allRequisitions.length)
          
          // Filter for travel-related requisitions more comprehensively
          travelRequisitions = allRequisitions.filter(
            (req) => req.typeOfProcurement === "travel" || 
                  (req.title && req.title.toLowerCase().includes("travel")) ||
                  (req.description && req.description.toLowerCase().includes("travel")) ||
                  (req.travelDetails && Object.keys(req.travelDetails).length > 0)
          )
          
          console.log("Filtered travel requisitions:", travelRequisitions.length)

          // Merge with dedicated travel requests, avoiding duplicates
          const existingIds = new Set(dedicatedTravelRequests.map((req) => req._id))
          const uniqueRequisitions = travelRequisitions.filter((req) => !existingIds.has(req._id))

          console.log("Unique travel requisitions:", uniqueRequisitions.length)
          
          // Combine both sources
          dedicatedTravelRequests = [...dedicatedTravelRequests, ...uniqueRequisitions]
        } catch (reqError) {
          console.warn("Error fetching travel requisitions:", reqError)
        }

        console.log("Total travel requests before processing:", dedicatedTravelRequests.length)

        // Process and normalize the data structure for consistency
        const processedRequests = dedicatedTravelRequests.map(request => {
          // If it's from requisitions, we need to adapt the structure
          if (request.typeOfProcurement === "travel" && request.travelDetails) {
            return {
              ...request,
              // Map requisition fields to travel request fields
              destination: request.travelDetails.destination || request.destination || 'N/A',
              purpose: request.travelDetails.purposeOfTravel || request.travelDetails.purpose || request.title || 'Travel Request',
              startDate: request.travelDetails.startDate,
              endDate: request.travelDetails.endDate,
              estimatedCost: request.travelDetails.estimatedCost || request.totalEstimatedCost || 0,
              urgency: request.travelDetails.urgency,
              transportMode: request.travelDetails.transportMode,
              accommodationType: request.travelDetails.accommodationType,
              description: request.travelDetails.additionalDescription || request.description,
              requesterName: request.travelDetails.requesterName || `${request.createdBy?.firstName || ''} ${request.createdBy?.lastName || ''}`.trim(),
              department: request.travelDetails.department || request.createdBy?.department || 'N/A',
            }
          }
          return request
        })

        console.log("Processed travel requests:", processedRequests.length)

        // Sort by creation date (newest first)
        processedRequests.sort((a, b) => new Date(b.createdAt || b.requestDate) - new Date(a.createdAt || a.requestDate))

        setRequests(processedRequests)

        // Calculate and set summary statistics
        const summaryStats = calculateSummary(processedRequests)
        setSummary(summaryStats)
        
        console.log("Summary stats:", summaryStats)
      } catch (err) {
        console.error("Error fetching travel requests:", err)
        setError("Failed to load travel requests. Please try again.")
      } finally {
        setLoading(false)
      }
    },
    [calculateSummary],
  )

  const fetchTravelReports = useCallback(async (filters = {}) => {
    setReportsLoading(true)
    setReportsError("")

    try {
      const response = await getTravelReports(filters)
      setReports(response.data || response)
    } catch (err) {
      console.error("Error fetching travel reports:", err)
      setReportsError("Failed to load travel reports. Please try again.")
    } finally {
      setReportsLoading(false)
    }
  }, [])

  const createTravelRequest = useCallback(
    async (travelData) => {
      try {
        console.log("Creating travel request with data:", travelData)

        const response = await apiCreateTravelRequest(travelData)
        console.log("Travel request created successfully:", response)

        // Refresh the requests list after creation
        await fetchTravelRequests()

        return response
      } catch (error) {
        console.error("Error in createTravelRequest hook:", error)

        // Re-throw with more context for the modal to handle
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Failed to create travel request"

        throw new Error(errorMessage)
      }
    },
    [fetchTravelRequests],
  )

  const updateRequestStatus = useCallback(
    async (requestId, status, additionalData = {}) => {
      try {
        let response

        if (status === "approved") {
          response = await approveTravelRequest(requestId, additionalData.comments || "")
        } else if (status === "rejected") {
          response = await rejectTravelRequest(requestId, additionalData.rejectionReason || "")
        } else {
          throw new Error(`Unsupported status update: ${status}`)
        }

        setRequests((prevRequests) => {
          const updatedRequests = prevRequests.map((req) =>
            req._id === requestId ? { ...req, status, currentStatus: status, ...additionalData } : req,
          )

          // Recalculate summary with updated requests
          const summaryStats = calculateSummary(updatedRequests)
          setSummary(summaryStats)

          return updatedRequests
        })

        return response
      } catch (error) {
        console.error(`Error updating request status to ${status}:`, error)
        setError(`Failed to ${status} request. Please try again.`)
        throw error
      }
    },
    [calculateSummary],
  )

  const addItinerary = useCallback(
    async (requestId, itineraryData) => {
      try {
        const response = await addItineraryToRequest(requestId, itineraryData)

        setRequests((prevRequests) => {
          const updatedRequests = prevRequests.map((req) =>
            req._id === requestId ? { ...req, itineraries: [...(req.itineraries || []), itineraryData] } : req,
          )

          // Recalculate summary with updated requests
          const summaryStats = calculateSummary(updatedRequests)
          setSummary(summaryStats)

          return updatedRequests
        })

        return response
      } catch (error) {
        console.error("Error adding itinerary:", error)
        setError("Failed to add itinerary. Please try again.")
        throw error
      }
    },
    [calculateSummary],
  )

  const refreshRequests = useCallback(() => {
    fetchTravelRequests()
  }, [fetchTravelRequests])

  // Initial load
  useEffect(() => {
    fetchTravelRequests()
  }, [fetchTravelRequests])

  return {
    requests,
    loading,
    error,
    summary,
    createTravelRequest,
    updateRequestStatus,
    addItinerary,
    refreshRequests,
    fetchTravelRequests,
    reports,
    reportsLoading,
    reportsError,
    fetchTravelReports,
  }
}

export default useTravelRequests